import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAllProperties, selectFeaturedProperties } from '../store/selectors/propertySelectors';
import { useTranslation } from '../utils/translations';
import { agentsAPI } from '../services/api';
import { formatInstagramDisplay, getInstagramUrl } from '../utils/instagramHelpers';
import { getPhoneUrl, getEmailUrl } from '../utils/contactHelpers';
import { getAgentProperties } from '../utils/agentHelpers';
import { resolveMediaUrl } from '../utils/mediaUrl';
import InstagramIcon from '../components/InstagramIcon/InstagramIcon';
import AgentPhoto from '../components/AgentPhoto/AgentPhoto';
import PropertyCard from '../components/PropertyCard/PropertyCard';
import { SkeletonAgent, SkeletonList } from '../components/loading';
import './AgentDetailsPage.css';

const AgentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const allProperties = useSelector(selectAllProperties);
  const featuredProperties = useSelector(selectFeaturedProperties);
  const [agent, setAgent] = useState(location.state?.agent || null);
  const [loading, setLoading] = useState(!location.state?.agent);
  const [error, setError] = useState(null);

  // Always fetch fresh agent data from API (location.state may be stale)
  useEffect(() => {
    let cancelled = false;

    if (location.state?.agent) {
      setAgent(location.state.agent);
      setLoading(false);
    }

    const fetchAgent = async () => {
      try {
        if (!location.state?.agent) {
          setLoading(true);
        }
        setError(null);
        const agentData = await agentsAPI.getById(id);
        if (cancelled) return;
        if (agentData) {
          setAgent(agentData);
        } else if (!location.state?.agent) {
          setError('Agent not found');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching agent:', err);
          if (!location.state?.agent) {
            setError(err.message || 'Failed to load agent');
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAgent();

    return () => {
      cancelled = true;
    };
  }, [id, location.state]);

  const recentlyViewed = featuredProperties.slice(0, 3);

  const agentProperties = useMemo(
    () => getAgentProperties(allProperties, agent),
    [allProperties, agent]
  );

  // Calculate property categories from agent's properties
  const categories = useMemo(() => {
    const categoryCounts = {};
    agentProperties.forEach(property => {
      const type = property.propertyType || 'other';
      categoryCounts[type] = (categoryCounts[type] || 0) + 1;
    });

    return Object.entries(categoryCounts).map(([type, count]) => ({
      label: t(`propertyTypes.${type}`) || type,
      count,
    }));
  }, [agentProperties, t]);

  if (loading) {
    return (
      <div className="agent-details-page">
        <div className="container" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
          <div style={{ maxWidth: 320, marginBottom: '2rem' }}>
            <SkeletonAgent />
          </div>
          <SkeletonList count={3} />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="agent-details-page">
        <div className="container">
          <div className="agent-not-found">
            <h2>{error || t('agents.notFound') || 'Agent not found'}</h2>
            <button onClick={() => navigate('/agents')} className="btn-primary">
              {t('agents.backToAgents') || 'Back to Agents'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-details-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <button onClick={() => navigate('/')} className="breadcrumb-link">
            {t('header.home')}
          </button>
          <span className="breadcrumb-separator">/</span>
          <button onClick={() => navigate('/agents')} className="breadcrumb-link">
            {t('header.agents')}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{agent.name}</span>
        </nav>

        <div className="agent-details-layout">
          <div className="agent-details-main">
            {/* Agent Info Section */}
            <div className="agent-info-section">
              <div className="agent-profile">
                <div className="agent-image-container">
                  <AgentPhoto
                    src={agent.image}
                    alt={agent.name}
                    variant="profile"
                    className="agent-profile-image"
                  />
                </div>
                <div className="agent-profile-info">
                  <div className="agent-heading">
                    <h1 className="agent-profile-name">{agent.name}</h1>
                    <div className="agent-count-pill">
                      {agentProperties.length} {t('agents.properties') || 'Prona'}
                    </div>
                  </div>
                  <p className="agent-profile-title">{agent.title}</p>
                  <div className="agent-contact-info">
                    {agent.mobile && (
                      <a href={getPhoneUrl(agent.mobile)} className="agent-contact-item agent-contact-link">
                        <span className="contact-icon">📞</span>
                        <span>{agent.mobile}</span>
                      </a>
                    )}
                    {agent.instagram && (
                      <a
                        href={getInstagramUrl(agent.instagram)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="agent-contact-item agent-contact-link"
                      >
                        <span className="contact-icon">
                          <InstagramIcon />
                        </span>
                        <span>{formatInstagramDisplay(agent.instagram)}</span>
                      </a>
                    )}
                    {agent.email && (
                      <a href={getEmailUrl(agent.email)} className="agent-contact-item agent-contact-link">
                        <span className="contact-icon">✉️</span>
                        <span>{agent.email}</span>
                      </a>
                    )}
                    <p className="agent-contact-item">
                      <span className="contact-icon">📍</span>
                      <span>{agent.city}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Properties Section */}
            <div className="agent-properties-section">
              <div className="section-header">
                <h2>
                  {t('agents.agentProperties') || 'Pronat nga'} {agent.name}
                </h2>
              </div>

              {agentProperties.length > 0 ? (
                <div className="properties-grid">
                  {agentProperties.map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
              ) : (
                <div className="no-properties">
                  <p>{t('agents.noProperties') || 'This agent has no properties listed yet.'}</p>
                </div>
              )}
            </div>
          </div>

          <aside className="agent-details-sidebar">
            <div className="sidebar-card">
              <h3 className="sidebar-title">{t('agents.featuredProperties') || 'Prona të Përzgjedhura'}</h3>
              <div className="sidebar-list">
                {featuredProperties.slice(0, 5).map((p) => (
                  <button
                    key={p.id}
                    className="sidebar-property"
                    onClick={() => navigate(`/property/${p.id}`)}
                    type="button"
                  >
                    <div className="sidebar-thumb">
                      {p.images && p.images[0] ? (
                        <img src={resolveMediaUrl(p.images[0])} alt={p.title} />
                      ) : (
                        <div className="sidebar-thumb-fallback">🏠</div>
                      )}
                    </div>
                    <div className="sidebar-property-info">
                      <div className="sidebar-badges">
                        <span className="sidebar-badge">
                          {p.transactionType === 'rent' ? (t('transactionTypes.rent') || 'Qira') : (t('transactionTypes.sale') || 'Shitje')}
                        </span>
                      </div>
                      <div className="sidebar-property-title">{p.title}</div>
                      <div className="sidebar-property-meta">{p.location}</div>
                    </div>
                  </button>
                ))}
                {featuredProperties.length === 0 && (
                  <div className="sidebar-empty">{t('agents.noFeatured') || 'No featured properties yet.'}</div>
                )}
              </div>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-title">{t('agents.categories') || 'Prona sipas kategorive'}</h3>
              <div className="sidebar-categories">
                {categories.map((c) => (
                  <div key={c.label} className="sidebar-category">
                    <span>{c.label}</span>
                    <span className="sidebar-category-count">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-title">{t('agents.recentlyViewed') || 'Recently Viewed'}</h3>
              <div className="sidebar-list">
                {recentlyViewed.map((p) => (
                  <button
                    key={p.id}
                    className="sidebar-property"
                    onClick={() => navigate(`/property/${p.id}`)}
                    type="button"
                  >
                    <div className="sidebar-thumb">
                      {p.images && p.images[0] ? (
                        <img src={resolveMediaUrl(p.images[0])} alt={p.title} />
                      ) : (
                        <div className="sidebar-thumb-fallback">🏠</div>
                      )}
                    </div>
                    <div className="sidebar-property-info">
                      <div className="sidebar-property-title">{p.title}</div>
                      <div className="sidebar-property-meta">{p.location}</div>
                    </div>
                  </button>
                ))}
                {recentlyViewed.length === 0 && (
                  <div className="sidebar-empty">{t('agents.noRecent') || 'No recently viewed properties yet.'}</div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AgentDetailsPage;
